'use-strict';

import { ILocationService, IScope } from 'angular';
import { RoleWithDetails, UsersAPIService } from '../../../shared/api/users-api.service';
import { ConfigService } from '../../../shared/config/config.service';
import { InterfaceService } from '../../../shared/interface/interface.service';
import { EFeatures } from '../../../src/app/@shared-types/tenant/enums';
import {
  FrontendUser,
  NewUserRequest,
  RoleAssignment,
  Subsidiary,
  UserSubsidiaryAssigment,
} from '../../../src/app/@shared-types/user';
import { ToastService, ToastType } from '../../../ui-components/toast/toast.service';
import { TierService } from '../../tenant/tier.service';

interface FrontendSubsidiaryAssignment extends Subsidiary {
  assigned: boolean,
}

interface UserFormScope extends IScope {
  additionalData: {
    newPassword: string,
    mailNotification: boolean,
    generatePasswort: boolean,
    initialPassword: string,
    assignedRoles: boolean[],
  },
  newUser: boolean,
  roles: RoleWithDetails[],
  user: FrontendUser | null,
  transmitting: boolean,
  userId: number | null,
  subsidiaries: {
    assignments: FrontendSubsidiaryAssignment[],
    mainSubsidiary: string,
  }
  submitForm: () => Promise<void>,
  deleteUser: (userId: number) => Promise<void>,
  mainSubsidiaryChange: () => void,
  features: {
    canUseLinkAPI: boolean,
    canUseExternalUser: boolean,
  },
}

/* @ngInject */
export default function UserFormController(
  toastService: ToastService,
  $scope: UserFormScope,
  usersApiService: UsersAPIService,
  interfaceService: InterfaceService,
  $location: ILocationService,
  $routeParams: angular.route.IRouteParamsService,
  configService: ConfigService,
  tierService: TierService,
): void {
  $scope.additionalData = {
    newPassword: '',
    generatePasswort: false,
    initialPassword: '',
    mailNotification: false,
    assignedRoles: [],
  };
  $scope.roles = [];
  $scope.user = null;
  $scope.newUser = false;
  $scope.transmitting = false;
  $scope.userId = null;
  $scope.subsidiaries = {
    assignments: configService.get('subsidiaries')?.map((s) => ({
      ...s,
      assigned: false,
    })) || [],
    mainSubsidiary: '',
  };
  $scope.features = {
    canUseLinkAPI: tierService.authorizedToUseFeature(EFeatures.API_LINK_ENTRY_IMPORT) || false,
    canUseExternalUser: tierService.authorizedToUseFeature(EFeatures.USER_EXTERNAL_ACCESS) || false,
  };

  const redirectToUserTable = () => {
    $location.path('/settings/users/table');
    interfaceService.applyScope($scope);
  };

  const getRoleAssignments = (userId: number): RoleAssignment[] => {
    if (!$scope.user) return [];

    const roleAssignments: { user_id: number, role_id: number }[] = [];
    $scope.additionalData.assignedRoles.forEach((r, roleId) => {
      if (!r || !$scope.user) return;
      roleAssignments.push({ user_id: userId, role_id: roleId });
    });

    return roleAssignments;
  };

  $scope.mainSubsidiaryChange = (): void => {
    const newMain = $scope.subsidiaries.assignments
      .find((s) => s.id === $scope.subsidiaries.mainSubsidiary);
    if (!newMain) return;

    newMain.assigned = true;
  };

  const getSubsidiaryAssignments = (
    userId: number,
  ): Omit<UserSubsidiaryAssigment, 'id'>[] => {
    const assignments: Omit<UserSubsidiaryAssigment, 'id'>[] = [];

    $scope.subsidiaries.assignments.forEach((s) => {
      const mainSubsidiary = $scope.subsidiaries.mainSubsidiary === s.id;
      if (!mainSubsidiary && !s.assigned) return;

      assignments.push({
        mainSubsidiary,
        subsidiaryId: s.id,
        userId,
      });
    });

    return assignments;
  };

  /** Add a new user with the form's data. */
  const addUser = async () => {
    $scope.transmitting = true;

    try {
      if (!$scope.user) return;

      const newUserRequest: NewUserRequest = {
        password: null,
        user: $scope.user,
        welcomeMail: $scope.additionalData.mailNotification,
      };

      delete (newUserRequest.user as any).id;

      if ($scope.additionalData.initialPassword && !$scope.additionalData.generatePasswort) {
        newUserRequest.password = $scope.additionalData.initialPassword;
      }

      const result = await usersApiService.addUser(newUserRequest);
      await usersApiService.updateRoleAssignments(getRoleAssignments(result.id));

      const subsidiaryAssignments = getSubsidiaryAssignments(result.id);
      await usersApiService.updateSubsidiaryAssignments(result.id, subsidiaryAssignments);

      $scope.transmitting = false;
      toastService.toast(ToastType.SUCCESS, 'Benutzeraccount wurde angelegt');
      redirectToUserTable();
    } catch (e) {
      $scope.transmitting = false;
      toastService.toast(ToastType.ERROR, 'Fehler beim Anlegen des Benutzeraccounts');
    }
  };

  /** Update the user with the form's data. */
  const updateUser = async (): Promise<void> => {
    try {
      if (!$scope.user) return;

      const newPassword = $scope.additionalData.newPassword || undefined;
      await usersApiService.updateUser($scope.user, newPassword);

      await usersApiService.updateRoleAssignments(getRoleAssignments($scope.user.id));

      const subsidiaryAssignments = getSubsidiaryAssignments($scope.user.id);
      await usersApiService.updateSubsidiaryAssignments($scope.user.id, subsidiaryAssignments);

      $scope.transmitting = false;
      toastService.toast(ToastType.SUCCESS, 'Benutzer gespeichert');

      redirectToUserTable();
    } catch (e) {
      $scope.transmitting = false;
      toastService.toast(ToastType.ERROR, 'Fehler beim Speichern des Benutzers');
    }
  };

  $scope.deleteUser = async (userId): Promise<void> => usersApiService.deleteUser(userId)
    .then(() => {
      toastService.toast(ToastType.SUCCESS, 'Der Benutzer wurde gelöscht');
      redirectToUserTable();
    });

  /**
   * Either add or update a user, depending on the form's mode.
   */
  $scope.submitForm = async (): Promise<void> => {
    $scope.transmitting = true;
    if ($scope.newUser) return addUser();
    return updateUser();
  };

  const initRoles = async (): Promise<void> => {
    const roles: RoleWithDetails[] = await usersApiService.getRoles();
    roles.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

    $scope.roles = roles;
  };

  /** Prepares the view's list of roles. Precheck items, if assignment is provided */
  const initRoleAssignments = (assignments?: { role_id: number, user_id: number }[]) => {
    $scope.roles.forEach((r) => {
      $scope.additionalData.assignedRoles[r.id] = false;

      if (assignments && assignments.some((a) => a.role_id === r.id)) {
        $scope.additionalData.assignedRoles[r.id] = true;
      }
    });
  };

  const initSubsidiaryAssignment = async (userId: number): Promise<void> => {
    const assignments = await usersApiService.getSubsidiaryAssignments(userId);

    assignments.forEach((a) => {
      if (a.mainSubsidiary) $scope.subsidiaries.mainSubsidiary = a.subsidiaryId;

      const sub = $scope.subsidiaries.assignments.find((s) => s.id === a.subsidiaryId);
      if (!sub) return;

      sub.assigned = true;
    });
  };

  const initEditUser = async (): Promise<void> => {
    const userId = $routeParams.userid;
    if (!userId) return;

    const roleAssignments = await usersApiService.getRoleAssignmentsForUser(userId);
    const user = await usersApiService.getUser(userId);

    $scope.user = {
      id: user.id,
      email: user.email,
      external_company_id: user.external_company_id,
      external_user: user.external_user,
      first_name: user.first_name,
      last_name: user.last_name,
      link_entry_accounts: user.link_entry_accounts,
      position: user.position,
      workflow_email: user.workflow_email,
      lock_state: user.lock_state,
    };

    await initSubsidiaryAssignment(userId);
    initRoleAssignments(roleAssignments);
  };

  const initNewUser = async (): Promise<void> => {
    $scope.user = {
      id: -1,
      email: '',
      external_company_id: '',
      external_user: false,
      first_name: '',
      last_name: '',
      link_entry_accounts: '',
      position: '',
      workflow_email: '',
      lock_state: false,
    };
    $scope.newUser = true;

    initRoleAssignments();
  };

  /**
   * Initialize the form by retrieving the roles first
   * and then get additonal data depedending on form mode.
   */
  const init = async () => {
    await initRoles();

    if ($routeParams.userid) return initEditUser();
    return initNewUser();
  };

  init();
}
